import type { RefObject } from 'react';
import type { KanbanColumn } from '../taskEditModal.types';

interface StatusDropdownProps {
  columns: KanbanColumn[];
  currentColumn?: KanbanColumn;
  currentColumnLabel: string;
  taskStatus?: string;
  show: boolean;
  onToggle: () => void;
  onSelect: (status: string) => void;
  dropdownRef: RefObject<HTMLDivElement | null>;
}

/**
 * Header status token + dropdown for the Task Edit Modal.
 * Click-outside is handled by the orchestrator via the passed `dropdownRef`.
 */
export function StatusDropdown({
  columns,
  currentColumn,
  currentColumnLabel,
  taskStatus,
  show,
  onToggle,
  onSelect,
  dropdownRef,
}: StatusDropdownProps) {
  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          // Chip = soft wash of the column color with matching ink; bases come
          // from theme tokens so the wash mixes over --color-surface and the ink
          // toward --color-text (dark in light themes, light in dark themes).
          color: currentColumn?.color ? `color-mix(in srgb, ${currentColumn.color} 60%, var(--color-text))` : 'var(--color-text-muted)',
          background: currentColumn?.color ? `color-mix(in srgb, ${currentColumn.color} 14%, var(--color-surface))` : 'var(--color-surface)',
          border: `1px solid ${currentColumn?.color ? `color-mix(in srgb, ${currentColumn.color} 25%, var(--color-border))` : 'var(--color-border)'}`,
          borderRadius: 8,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'transform 0.15s, box-shadow 0.15s',
          transform: show ? 'translateY(-1px)' : 'translateY(0)',
          boxShadow: show ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          if (!show) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
          }
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: currentColumn?.color || 'var(--color-border-strong)' }} />
        {currentColumnLabel}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, transform: show ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {show && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0, 0, 0, 0.15))',
          minWidth: 140,
          zIndex: 10002,
          overflow: 'hidden',
        }}>
          {columns
            .filter(col => col.status !== taskStatus)
            .map(col => (
            <button
              key={col.id}
              type="button"
              onClick={() => onSelect(col.status)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '10px 12px',
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'uppercase',
                color: 'var(--color-text)',
                background: 'var(--color-surface)',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'font-weight 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.fontWeight = '600';
                e.currentTarget.style.background = 'var(--color-surface-hover)';
              }}
              onMouseLeave={(e) => {
                // Restore the CURRENT base styles (weight 500 / surface) — the old
                // values left hovered rows permanently lighter and theme-tinted.
                e.currentTarget.style.fontWeight = '500';
                e.currentTarget.style.background = 'var(--color-surface)';
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: col.color || 'var(--color-border-strong)' }} />
              {col.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
